import { notNullOrUndefined } from "@core/utils";
import { IValidation } from "@generation/core";
import {
  AmountConditionType,
  AmountConditionValidation,
  BsnValidation,
  CompareFailValidation,
  DateValidation,
  EmailValidation,
  EqualsValueValidation,
  HasMaxLengthValidation,
  IbanValidation,
  IsRequiredValidation,
  KvkNummerValidation,
  MinimumDateValidation,
  NoFutureDateValidation,
  PhoneValidation,
  RegexValidation,
  StartDateLessOrEqualToEndDateValidation,
  StartDateLessThenEndDateValidation
} from "@generation/form";
import { CustomErrorMessage } from "@generation/form/src/Validations/ValidationHelper";
import { DateRangeOperator } from "../client/enums/dateRangeOperator";
import { CompareFieldToNumberValidation } from "../client/model/validation/compareFieldToNumberValidation";
import { CompareFieldValidation } from "../client/model/validation/compareFieldValidation";
import { DateRangeFieldValidation } from "../client/model/validation/dateRangeFieldValidation";
import { FieldValidation } from "../client/model/validation/validationField";

export class ValidationBuilder {
  private readonly validations: IValidation[] = [];

  constructor(private readonly type: string) {}

  private getCustomErrorMessage(customErrorMessage?: string): CustomErrorMessage | undefined {
    return customErrorMessage ? (_label: string) => customErrorMessage : undefined;
  }

  addIsRequired(isRequired?: FieldValidation<boolean> | null): this {
    if (isRequired?.value === true) {
      this.validations.push(
        new IsRequiredValidation(undefined, this.getCustomErrorMessage(isRequired?.errorMessage))
      );
    }

    if (this.type === "date") {
      this.validations.push(new DateValidation(isRequired?.value === true));
    }

    return this;
  }

  addNoFutureDateValidation(noFutureDateValidation?: FieldValidation<boolean> | null): this {
    if (noFutureDateValidation?.value === true) {
      this.validations.push(
        new NoFutureDateValidation(this.getCustomErrorMessage(noFutureDateValidation?.errorMessage))
      );
    }
    return this;
  }

  addMinimumDateValidation(minimumDate?: FieldValidation<string> | null): this {
    if (minimumDate?.value) {
      this.validations.push(
        new MinimumDateValidation(minimumDate?.value, this.getCustomErrorMessage(minimumDate?.errorMessage))
      );
    }
    return this;
  }

  addMaxLengthValidation(
    maxLength?: FieldValidation<number> | null,
    limitMaxLengthOnInputs?: FieldValidation<boolean> | null
  ): this {
    if (notNullOrUndefined(maxLength)) {
      this.validations.push(
        new HasMaxLengthValidation(
          maxLength?.value,
          this.getCustomErrorMessage(maxLength?.errorMessage),
          limitMaxLengthOnInputs?.value
        )
      );
    }
    return this;
  }

  addDateRangeValidation(dateRangeValidation?: DateRangeFieldValidation | null): this {
    if (dateRangeValidation) {
      if (dateRangeValidation.operator === DateRangeOperator.LessOrEqual) {
        this.validations.push(
          new StartDateLessOrEqualToEndDateValidation(
            dateRangeValidation.endDateProperty,
            dateRangeValidation.endDateLabel
          )
        );
      } else {
        this.validations.push(
          new StartDateLessThenEndDateValidation(
            dateRangeValidation.endDateProperty,
            dateRangeValidation.endDateLabel
          )
        );
      }
    }
    return this;
  }

  addBSNValidation(isBSN?: FieldValidation<boolean> | null): this {
    if (isBSN?.value === true) {
      this.validations.push(new BsnValidation(false, this.getCustomErrorMessage(isBSN?.errorMessage)));
    }
    return this;
  }

  addKvkValidation(kvkValidation?: FieldValidation<boolean> | null): this {
    if (kvkValidation?.value === true) {
      this.validations.push(
        new KvkNummerValidation(false, this.getCustomErrorMessage(kvkValidation?.errorMessage))
      );
    }
    return this;
  }

  addRegexValidation(regex?: FieldValidation<string> | null): this {
    if (regex?.value) {
      this.validations.push(
        new RegexValidation(false, regex?.value, this.getCustomErrorMessage(regex?.errorMessage))
      );
    }
    return this;
  }

  addGreaterThanValueValidation(greaterThanValue?: CompareFieldToNumberValidation | null): this {
    if (greaterThanValue && notNullOrUndefined(greaterThanValue?.value)) {
      this.validations.push(
        new AmountConditionValidation(
          greaterThanValue.value,
          AmountConditionType.GreaterThan,
          this.getCustomErrorMessage(greaterThanValue?.errorMessage),
          greaterThanValue.amountPrefix
        )
      );
    }
    return this;
  }

  addGreaterThanOrEqualValueValidation(
    greaterThanOrEqualValue?: CompareFieldToNumberValidation | null
  ): this {
    if (greaterThanOrEqualValue && notNullOrUndefined(greaterThanOrEqualValue?.value)) {
      this.validations.push(
        new AmountConditionValidation(
          greaterThanOrEqualValue.value,
          AmountConditionType.GreaterThanOrEqual,
          this.getCustomErrorMessage(greaterThanOrEqualValue?.errorMessage),
          greaterThanOrEqualValue.amountPrefix
        )
      );
    }
    return this;
  }

  addSmallerThanValueValidation(smallerThanValue?: CompareFieldToNumberValidation | null): this {
    if (smallerThanValue && notNullOrUndefined(smallerThanValue?.value)) {
      this.validations.push(
        new AmountConditionValidation(
          smallerThanValue.value,
          AmountConditionType.SmallerThan,
          this.getCustomErrorMessage(smallerThanValue?.errorMessage),
          smallerThanValue.amountPrefix
        )
      );
    }
    return this;
  }

  addSmallerThanOrEqualValueValidation(
    smallerThanOrEqualValue?: CompareFieldToNumberValidation | null
  ): this {
    if (smallerThanOrEqualValue && notNullOrUndefined(smallerThanOrEqualValue?.value)) {
      this.validations.push(
        new AmountConditionValidation(
          smallerThanOrEqualValue.value,
          AmountConditionType.SmallerThanOrEqual,
          this.getCustomErrorMessage(smallerThanOrEqualValue?.errorMessage),
          smallerThanOrEqualValue.amountPrefix
        )
      );
    }
    return this;
  }

  addEqualsValueValidation(
    fieldName: string | null,
    value: string | null,
    customErrorMessage?: CustomErrorMessage
  ): this {
    if (fieldName && value) {
      this.validations.push(new EqualsValueValidation(fieldName, value, customErrorMessage));
    }
    return this;
  }

  addCompareValidation(compare?: CompareFieldValidation | null): this {
    if (compare) {
      this.validations.push(
        new CompareFailValidation(
          false,
          compare.value,
          compare.operator,
          this.getCustomErrorMessage(compare.errorMessage)
        )
      );
    }
    return this;
  }

  addIbanValidation(ibanValidation?: FieldValidation<boolean> | null): this {
    if (ibanValidation?.value === true) {
      this.validations.push(
        new IbanValidation(false, this.getCustomErrorMessage(ibanValidation?.errorMessage))
      );
    }
    return this;
  }

  addEmailValidation(emailValidation?: FieldValidation<boolean> | null): this {
    if (emailValidation?.value === true) {
      this.validations.push(
        new EmailValidation(false, this.getCustomErrorMessage(emailValidation?.errorMessage))
      );
    }
    return this;
  }

  addPhoneValidation(
    phoneValidation?: FieldValidation<boolean> | null,
    isRequired?: FieldValidation<boolean> | null
  ): this {
    if (phoneValidation?.value === true) {
      this.validations.push(
        new PhoneValidation(false, this.getCustomErrorMessage(phoneValidation?.errorMessage))
      );
    }
    return this;
  }

  build(): IValidation[] {
    return this.validations;
  }
}
